import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider, useLang } from "@/lib/i18n";
import { AuthProvider } from "@/lib/auth";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Home from "@/pages/Home";

const named = (load, name) => lazy(() => load().then((m) => ({ default: m[name] })));
const owner = () => import("@/pages/Owner");
const admin = () => import("@/pages/admin/Admin");
const legal = () => import("@/pages/Legal");
const Browse = lazy(() => import("@/pages/Browse"));
const BusinessProfile = lazy(() => import("@/pages/BusinessProfile"));
const MyEnquiries = lazy(() => import("@/pages/MyEnquiries"));
const Removed = lazy(() => import("@/pages/Removed"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const Poster = lazy(() => import("@/pages/Poster"));
const OwnerHome = named(owner, "OwnerHome");
const OwnerNew = named(owner, "OwnerNew");
const OwnerBusiness = named(owner, "OwnerBusiness");
const OwnerEdit = named(owner, "OwnerEdit");
const Admin = named(admin, "Admin");
const AdminBusiness = named(admin, "AdminBusiness");
const PrivacyPage = named(legal, "PrivacyPage");
const TermsPage = named(legal, "TermsPage");
const RefundsPage = named(legal, "RefundsPage");
const ContactPage = named(legal, "ContactPage");
const JobsPage = lazy(() => import("@/pages/Jobs"));
const TownCategory = lazy(() => import("@/pages/TownCategory"));
const RenewPage = lazy(() => import("@/pages/Renew"));
const WhyPage = lazy(() => import("@/pages/Why"));
const MogaPage = lazy(() => import("@/pages/MogaPage"));
const QRedirect = lazy(() => import("@/pages/QRedirect"));

const ROUTES = [
  ["", Home], ["browse", Browse], ["b/:id", BusinessProfile], ["s/:slug", ShopPage], ["poster/:id", Poster], ["my", MyEnquiries], ["removed", Removed],
  ["how-it-works", HowItWorks], ["why-moga-360", WhyPage], ["owner", OwnerHome], ["owner/new", OwnerNew], ["owner/b/:id", OwnerBusiness],
  ["owner/b/:id/edit", OwnerEdit], ["admin", Admin], ["admin/b/:id", AdminBusiness],
  ["privacy", PrivacyPage], ["terms", TermsPage], ["refunds", RefundsPage], ["contact", ContactPage], ["jobs", JobsPage], ["about-moga/:slug", MogaPage], ["q/:code", QRedirect], ["renew/:slug", RenewPage], [":town/:category", TownCategory],
];

const DocLang = () => {
  const lang = useLang();
  const { pathname } = useLocation();
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dataset.lang = lang;
  }, [lang]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function AppRouter() {
  return (
    <Suspense fallback={<div className="min-h-screen" data-testid="page-loading" />}>
      <Routes>
        {["", "/pa", "/hi"].flatMap((p) =>
          ROUTES.map(([path, C]) => <Route key={`${p}/${path}`} path={`${p}/${path}`} element={<C />} />),
        )}
        <Route path="*" element={<Home />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <BrowserRouter>
      <LangProvider>
        <AuthProvider>
          <DocLang />
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </LangProvider>
    </BrowserRouter>
    </GoogleOAuthProvider>
  );
}
